import React, { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Modal, Input, Button, Tag, Tooltip, Icon, Popover, Spin } from 'antd'
import { ImagePreview } from '../../../../../../components'
import EditCharacterModal from './EditCharacterModal'
import './CharacterManagerModal.scss'

const CharacterManagerModal = ({
  visible,
  onClose,
  characters = [],
  onAddCharacter,
  isFetchingCharacters,
  fetchCharacters,
  videoId
}) => {
  const user = useSelector(state => state.user)
  const isSuperAdmin = user?.info?.is_super_admin || user?.info?.isSuperAdmin

  const [searchText, setSearchText] = useState('')
  const [editingCharacter, setEditingCharacter] = useState(null)

  // --- Filtering ---
  const { systemCharacters, customCharacters } = useMemo(() => {
    const q = searchText.toLowerCase()
    const filtered = characters.filter(c => {
      const attrs = c.attributes || {}
      return (
        (c.name || '').toLowerCase().includes(q) ||
        (c.gender || '').toLowerCase().includes(q) ||
        (attrs.original_prompt || '').toLowerCase().includes(q) ||
        (attrs.character_apparel || '').toLowerCase().includes(q)
      )
    })

    return {
      systemCharacters: filtered.filter(c => c.type === 'system'),
      customCharacters: filtered.filter(c => c.type !== 'system')
    }
  }, [characters, searchText])

  // --- Handle edit success ---
  const handleEditSuccess = () => {
    setEditingCharacter(null)
    if (fetchCharacters) {
      fetchCharacters(true)
    }
  }

  // --- Render Card ---
  const renderCharacterCard = char => {
    const attrs = char.attributes || {}
    const pic = attrs.profile_pic || ''
    
    // --- Collect multiple images ---
    let imagesArray = []
    if (attrs.images && Array.isArray(attrs.images) && attrs.images.length > 0) {
      imagesArray = [...attrs.images]
      if (pic && !imagesArray.includes(pic)) {
         imagesArray.unshift(pic)
      }
    } else if (pic) {
      imagesArray = [pic]
    }

    const prompt = attrs.original_prompt || ''

    // Helper to safely parse the apparel string/array
    let apparelList = []
    try {
      if (Array.isArray(attrs.character_apparel)) {
        apparelList = attrs.character_apparel
      } else if (typeof attrs.character_apparel === 'string') {
        let cleanStr = attrs.character_apparel.trim()

        if (cleanStr.startsWith('[') && cleanStr.endsWith(']')) {
          cleanStr = cleanStr.substring(1, cleanStr.length - 1)
          apparelList = cleanStr
            .split(',')
            .map(item => {
              return item.trim().replace(/^['"]|['"]$/g, '')
            })
            .filter(Boolean)
        } else {
          apparelList = cleanStr
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        }
      }
    } catch (e) {
      console.warn('Failed to parse apparel:', attrs.character_apparel)
      apparelList = []
    }

    let genderColor = 'other'
    if (char.gender && char.gender.toLowerCase() === 'male')
      genderColor = 'male'
    if (char.gender && char.gender.toLowerCase() === 'female')
      genderColor = 'female'

    return (
      <div key={char.id || char.name} className="character-card-wrapper">
        <div className="card-image-box">
          <ImagePreview
            src={pic}
            images={imagesArray.length > 0 ? imagesArray : undefined}
            alt={char.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {imagesArray.length > 1 && (
            <div className="card-images-count">
              <Icon type="switcher" /> {imagesArray.length} Images
            </div>
          )}
          {/* Edit button overlay (hidden for system characters unless superadmin) */}
          {(char.type !== 'system' || isSuperAdmin) && (
            <div className="card-edit-overlay">
              <Tooltip title="Edit Character">
                <button
                  className="card-edit-btn"
                  onClick={e => {
                    e.stopPropagation()
                    setEditingCharacter(char)
                  }}
                >
                  <Icon type="edit" />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
        <div className="card-info">
          <div className="card-header-row">
            <Tooltip title={char.name}>
              <div className="card-name">{char.name}</div>
            </Tooltip>
            {char.gender && (
              <span className={`gender-tag ${genderColor}`}>
                {char.gender.slice(0, 1)}
              </span>
            )}
          </div>

          <Tooltip title={prompt} placement="bottomLeft">
            <div className="card-prompt">
              {prompt || 'No description available.'}
            </div>
          </Tooltip>

          {apparelList && apparelList.length > 0 && (
            <div className="card-apparel">
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px',
                  marginTop: '4px'
                }}
              >
                {apparelList.slice(0, 3).map((item, idx) => (
                  <Tag
                    key={idx}
                    style={{ margin: 0, fontSize: '10px', background: '#fff' }}
                  >
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                  </Tag>
                ))}
                {apparelList.length > 3 && (
                  <Popover
                    content={
                      <div
                        style={{
                          maxWidth: '200px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '4px'
                        }}
                      >
                        {apparelList.slice(3).map((item, idx) => (
                          <Tag
                            key={idx}
                            style={{ margin: 0, fontSize: '10px' }}
                          >
                            {item.charAt(0).toUpperCase() + item.slice(1)}
                          </Tag>
                        ))}
                      </div>
                    }
                    title="More Apparel"
                    trigger="hover"
                  >
                    <Tag
                      style={{ margin: 0, fontSize: '10px', cursor: 'pointer' }}
                    >
                      +{apparelList.length - 3}
                    </Tag>
                  </Popover>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <Modal
        maskClosable={true}
        visible={visible}
        onCancel={onClose}
        footer={[
          <Button key="close" type="default" onClick={onClose} size="large">
            Close
          </Button>
        ]}
        width={1000}
        centered
        className="character-manager-modal"
        title={null}
        closable={false}
      >
        {/* Header Area */}
        <div className="ant-modal-header">
          <div className="modal-title">
            <div className="modal-icon">
              <Icon type="team" />
            </div>
            Manage Characters
          </div>
        </div>

        {/* Sticky Topbar */}
        <div className="manage-modal-topbar">
          <Input
            className="custom-search-bar"
            size="large"
            placeholder="Search by name, prompt or apparel..."
            suffix={<Icon type="search" />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
          />
          <Button
            type="primary"
            icon="plus"
            onClick={onAddCharacter}
            className="add-character-btn"
          >
            Add Character
          </Button>
        </div>

        {/* Content */}
        <div className="manager-content-scroll">
          {isFetchingCharacters ? (
            <div className="empty-state">
              <Spin size="large" tip="Fetching characters..." />
            </div>
          ) : systemCharacters.length > 0 || customCharacters.length > 0 ? (
            <div className="character-groups-container">
              {systemCharacters.length > 0 && (
                <div className="character-section">
                  <div className="section-title">
                    <Icon type="thunderbolt" style={{ color: '#faad14' }} />
                    System Characters
                    <span className="count-tag">{systemCharacters.length}</span>
                  </div>
                  <div className="characters-grid">
                    {systemCharacters.map(renderCharacterCard)}
                  </div>
                </div>
              )}

              {customCharacters.length > 0 && (
                <div className="character-section">
                  <div className="section-title">
                    <Icon type="user" style={{ color: '#1890ff' }} />
                    My Characters
                    <span className="count-tag">{customCharacters.length}</span>
                  </div>
                  <div className="characters-grid">
                    {customCharacters.map(renderCharacterCard)}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">
                <span role="img" aria-label="empty">
                  👻
                </span>
              </div>
              <div className="empty-title">
                {searchText ? 'No matches found' : 'No characters yet'}
              </div>
              <div className="empty-desc">
                {searchText
                  ? `We couldn't find any characters matching "${searchText}". Try a different term.`
                  : 'Get started by adding your first character to the library.'}
              </div>
              {!searchText && (
                <Button
                  type="primary"
                  style={{ marginTop: 20 }}
                  onClick={onAddCharacter}
                >
                  Create Character
                </Button>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Edit Character Modal */}
      <EditCharacterModal
        visible={!!editingCharacter}
        character={editingCharacter}
        videoId={videoId}
        onClose={() => setEditingCharacter(null)}
        onUpdated={handleEditSuccess}
      />
    </>
  )
}

export default CharacterManagerModal
